import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { inlineTags, containerTags, selfClosingTags } from "./sanitize";

type ErrorType = {
  line: number;
  message: string;
  type: string;
};

type StackType = {
  line: number;
  tag: string;
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface HtmlTextEditorProps {
  content: string;
  onContentChange: (value: string) => void;
  onValidationChange: (value: boolean) => void;
}
const HTMLTextEditor = ({
  content,
  onContentChange,
  onValidationChange,
}: HtmlTextEditorProps) => {
  const [errors, setErrors] = useState<ErrorType[] | undefined>([]);
  const [lineCount, setLineCount] = useState(0);
  const [visualLineCount, setVisualLineCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const hasAutoFormatted = useRef(false);

  const debouncedContent = useDebounce(content, 500);

  const syncLineNumbers = useCallback(() => {
    if (textareaRef.current && lineNumbersRef.current) {
      const textarea = textareaRef.current;
      const lineNumbers = lineNumbersRef.current;

      lineNumbers.scrollTop = textarea.scrollTop;

      const textareaHeight = textarea.scrollHeight;
      lineNumbers.style.height = textareaHeight + "px";
    }
  }, []);

  const autoResize = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";

      syncLineNumbers();
    }
  }, [syncLineNumbers]);

  const handleScroll = useCallback(() => {
    syncLineNumbers();
  }, [syncLineNumbers]);

  const calculateVisualLines = useCallback(() => {
    if (!textareaRef.current || !content) return 1;

    const textarea = textareaRef.current;
    const newContent = content;
    const logicalLines = newContent.split("\n");

    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.visibility = "hidden";
    tempDiv.style.whiteSpace = "pre-wrap";
    tempDiv.style.wordWrap = "break-word";
    tempDiv.style.width = textarea.clientWidth - 56 + "px";
    tempDiv.style.fontSize = "14px";
    tempDiv.style.fontFamily = "monospace";
    tempDiv.style.lineHeight = "1.5";
    tempDiv.style.padding = "0";
    tempDiv.style.margin = "0";
    tempDiv.style.border = "none";

    document.body.appendChild(tempDiv);

    let totalVisualLines = 0;
    const lineHeight = 21;

    logicalLines.forEach((line) => {
      if (line.length === 0) {
        totalVisualLines += 1;
      } else {
        tempDiv.textContent = line;
        const divHeight = tempDiv.offsetHeight;
        const wrappedLines = Math.max(1, Math.round(divHeight / lineHeight));
        totalVisualLines += wrappedLines;
      }
    });

    document.body.removeChild(tempDiv);
    return totalVisualLines;
  }, [content]);

  const getVisualLineNumber = useCallback(
    (textContent: string, charPosition: number) => {
      if (!textareaRef.current) return 1;

      const textarea = textareaRef.current;
      const textBeforePosition = textContent.substring(0, charPosition);
      const logicalLines = textBeforePosition.split("\n");

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      tempDiv.style.whiteSpace = "pre-wrap";
      tempDiv.style.wordWrap = "break-word";
      tempDiv.style.width = textarea.clientWidth - 56 + "px";
      tempDiv.style.fontSize = "14px";
      tempDiv.style.fontFamily = "monospace";
      tempDiv.style.lineHeight = "1.5";
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      tempDiv.style.border = "none";

      document.body.appendChild(tempDiv);

      let visualLineNumber = 0;
      const lineHeight = 21;

      for (let i = 0; i < logicalLines.length - 1; i++) {
        const line = logicalLines[i];
        if (line.length === 0) {
          visualLineNumber += 1;
        } else {
          tempDiv.textContent = line;
          const divHeight = tempDiv.offsetHeight;
          const wrappedLines = Math.max(1, Math.round(divHeight / lineHeight));
          visualLineNumber += wrappedLines;
        }
      }

      const lastLine = logicalLines[logicalLines.length - 1];
      if (lastLine.length > 0) {
        tempDiv.textContent = lastLine;
        const divHeight = tempDiv.offsetHeight;
        const lineHeight = 21;
        const wrappedLines = Math.max(1, Math.round(divHeight / lineHeight));

        const charInLastLine =
          charPosition - textBeforePosition.lastIndexOf("\n") - 1;
        if (charInLastLine >= 0 && wrappedLines > 1) {
          const charsPerLine = Math.ceil(lastLine.length / wrappedLines);
          const visualLineInWrap = Math.floor(charInLastLine / charsPerLine);
          visualLineNumber += visualLineInWrap + 1;
        } else {
          visualLineNumber += 1;
        }
      } else {
        visualLineNumber += 1;
      }

      document.body.removeChild(tempDiv);
      return visualLineNumber;
    },
    [],
  );

  const validateHTML = useCallback(
    (htmlContent: string) => {
      const lines = htmlContent.split("\n");
      const foundErrors: ErrorType[] = [];

      const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
      const stack: StackType[] = [];

      let currentPosition = 0;

      lines.forEach((line) => {
        let match;
        tagRegex.lastIndex = 0;

        while ((match = tagRegex.exec(line)) !== null) {
          const tagName = match[1].toLowerCase();
          const fullTag = match[0];
          const isClosingTag = fullTag.startsWith("</");
          const isSelfClosing = selfClosingTags.includes(tagName);

          const tagPosition = currentPosition + match.index;
          const visualLine = getVisualLineNumber(htmlContent, tagPosition);

          if (!inlineTags.includes(tagName) && !isSelfClosing) {
            foundErrors.push({
              line: visualLine,
              message: `Etiqueta no permitida: <${tagName}>`,
              type: "forbidden-tag",
            });
            continue;
          }

          if (!isClosingTag && !isSelfClosing) {
            stack.push({ tag: tagName, line: visualLine });
          } else if (isClosingTag) {
            if (stack.length === 0) {
              foundErrors.push({
                line: visualLine,
                message: `Etiqueta de cierre sin apertura: </${tagName}>`,
                type: "unopened-tag",
              });
            } else {
              const lastOpened = stack.pop();
              if (lastOpened && lastOpened.tag !== tagName) {
                foundErrors.push({
                  line: visualLine,
                  message: `Etiqueta mal cerrada: esperaba </${lastOpened.tag}> pero encontró </${tagName}>`,
                  type: "mismatched-tag",
                });
              }
            }
          }
        }

        currentPosition += line.length + 1;
      });

      stack.forEach((unclosed) => {
        foundErrors.push({
          line: unclosed.line,
          message: `Etiqueta no cerrada: <${unclosed.tag}>`,
          type: "unclosed-tag",
        });
      });

      return foundErrors;
    },
    [getVisualLineNumber],
  );

  const formatHTML = useCallback(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${content}</div>`, "text/html");
    const root = doc.body.firstChild as HTMLElement;

    const INDENT = "  ";

    function formatNode(node: ChildNode, depth: number): string {
      const indent = INDENT.repeat(depth);

      if (node.nodeType === Node.TEXT_NODE) {
        const trimmed = node.textContent?.trim();
        return trimmed ? indent + trimmed : "";
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tagName = el.tagName.toLowerCase();

        const tagOpen = `<${tagName}${[...el.attributes]
          .map((attr) => ` ${attr.name}="${attr.value}"`)
          .join("")}>`;
        const tagClose = `</${tagName}>`;

        const children = [...el.childNodes];
        const isContainer = containerTags.includes(tagName);

        if (children.length === 0) {
          return `${indent}${tagOpen.replace(/>$/, " />")}`;
        }

        if (!isContainer) {
          // No indentación adicional, mantener en la misma línea
          const inner = children
            .map((child) => {
              if (child.nodeType === Node.TEXT_NODE)
                return child.textContent?.trim();
              return formatNode(child, 0).trim();
            })
            .join("");
          return `${indent}${tagOpen}${inner}${tagClose}`;
        }

        // Si es contenedor, aplicar indentación a los hijos
        const childLines = children
          .map((child) => formatNode(child, depth + 1))
          .filter(Boolean)
          .join("\n");

        return `${indent}${tagOpen}\n${childLines}\n${indent}${tagClose}`;
      }

      return "";
    }

    const formattedHTML = [...root.childNodes]
      .map((node) => formatNode(node, 0))
      .filter(Boolean)
      .join("\n");

    onContentChange(formattedHTML.trim());
  }, [content, onContentChange]);

  useEffect(() => {
    if (!hasAutoFormatted.current && content.trim()) {
      hasAutoFormatted.current = true;
      formatHTML();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onContentChange(e.target.value);
  };

  useEffect(() => {
    const lines = content.split("\n").length;
    const chars = content.length;
    setLineCount(lines);
    setCharCount(chars);

    const visualLines = calculateVisualLines();
    setVisualLineCount(visualLines);
  }, [content, calculateVisualLines]);

  useEffect(() => {
    autoResize();
  }, [content, autoResize]);

  useEffect(() => {}, []);

  useEffect(() => {
    const validationErrors = validateHTML(debouncedContent);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      onValidationChange(false);
    } else {
      setErrors(undefined);
      onValidationChange(true);
    }
  }, [debouncedContent, validateHTML, onValidationChange]);

  useEffect(() => {
    syncLineNumbers();
  }, [content, syncLineNumbers]);

  return (
    <div className="rounded-b-2xl flex flex-col bg-neutral-object">
      <div className="flex justify-between items-center px-4 py-2">
        <Button
          onClick={formatHTML}
          className="flex items-center gap-2 cursor-pointer"
          size="sm"
        >
          <Icon
            type="far"
            name="code-simple"
            className="w-3.5 h-3.5 text-sm font-normal mt-0.5 flex-shrink-0"
          />
          Formatear HTML
        </Button>
        <div className="text-sm text-body-loud space-x-4">
          <span>Líneas: {lineCount}</span>
          <span>Caracteres: {charCount}</span>
        </div>
      </div>
      {errors !== undefined && errors?.length > 0 && (
        <div className="p-4 space-y-2">
          {errors.map((error, index) => (
            <div
              key={index}
              className="flex items-start gap-2 p-2 bg-surface-danger-accent border border-surface-danger-accent rounded text-sm"
            >
              <Icon
                type="far"
                name="circle-exclamation"
                className="w-4 h-4 text-sm font-normal mt-0.5 flex-shrink-0 text-danger"
              />
              <div>
                <span className="font-semibold text-danger/75">
                  Línea {error.line}:
                </span>
                <span className="text-danger ml-2">{error.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="relative rounded-b-2xl">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onScroll={handleScroll}
          className="w-full appearance-none resize-none focus:outline-none border-t border-neutral-quiet pl-14 overflow-hidden text-body-loud"
          style={{
            lineHeight: "1.5",
            minHeight: "100px",
            fontSize: "14px",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        />

        <div
          ref={lineNumbersRef}
          className="absolute left-0 top-0 w-12 bg-neutral-object-accent-silent border-r border-neutral-quiet text-xs text-body-quiet font-mono pointer-events-none overflow-hidden"
          style={{
            lineHeight: "1.5",
            fontSize: "14px",
            fontFamily: "monospace",
            minHeight: "100px",
          }}
        >
          {Array.from({ length: visualLineCount }, (_, i) => (
            <div
              key={i}
              className="px-2 text-right"
              style={{
                lineHeight: "1.5",
                height: "21px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HTMLTextEditor;
